import React, { useLayoutEffect, useRef, useState } from 'react';
import ColumnOptionsEditor from '../ColumnOptionsEditor';
import { NtIcon } from '../NotionUI';
import { TYPE_ICONS } from '../../utils/projectView';
import { useLanguage } from '../../i18n/LanguageContext';

export const OPTION_TYPES = ['select', 'status', 'multi_select'];
// Tipi creabili da zero e tipi tra cui si può convertire (devono coincidere col backend).
export const CREATABLE_TYPES = ['text', 'number', 'select', 'status', 'multi_select', 'date', 'daterange', 'person', 'checkbox', 'url', 'email', 'phone', 'files', 'task_relation', 'created_time', 'updated_time', 'created_by', 'updated_by'];
export const CONVERTIBLE_TYPES = ['text', 'url', 'email', 'phone', 'number', 'select', 'status', 'multi_select', 'date', 'checkbox', 'person'];
// Raggruppamento dei tipi nel selettore.
const TYPE_GROUPS = [
  ['basic', ['text', 'number', 'url', 'email', 'phone', 'checkbox']],
  ['choice', ['select', 'status', 'multi_select', 'person']],
  ['dates', ['date', 'daterange']],
  ['advanced', ['files', 'task_relation']],
  ['auto', ['created_time', 'updated_time', 'created_by', 'updated_by']],
];

/**
 * Menu di una colonna. Senza `column` crea una colonna nuova (nome, tipo, opzioni).
 * Con una colonna esistente si apre come il menu delle proprietà di Notion: in cima il nome
 * (si rinomina scrivendo e premendo Invio) e l'icona del tipo; poi "Modifica colonna" (tipo e opzioni, in
 * una schermata a parte), le scorciatoie (filtra, ordina, raggruppa, nascondi), l'inserimento a
 * sinistra o a destra, duplica ed elimina. `actions` è l'elenco delle azioni disponibili, per chiave.
 */
function ColumnMenu({ column, actions, initialView = 'main', onSubmit, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(() => ({
    name: column ? column.name : '',
    type: column ? column.type : 'text',
    options: column ? column.options.map((o) => ({ ...o })) : [],
  }));
  const [view, setView] = useState(column && column.type !== 'title' ? initialView : 'main'); // 'main' | 'edit' | 'sort'

  const isNew = column === null;

  // Il contenitore della tabella scorre e taglia i popup: li posiziono rispetto alla finestra,
  // sotto l'intestazione della colonna, restando dentro lo schermo.
  const formRef = useRef(null);
  const [place, setPlace] = useState(null);
  useLayoutEffect(() => {
    const anchor = formRef.current?.parentElement;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(formRef.current.offsetWidth || 340, window.innerWidth - 16);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const top = Math.min(rect.bottom + 4, Math.max(8, window.innerHeight - 160));
    setPlace({ top, left, maxHeight: window.innerHeight - top - 12 });
  }, [view]);
  const defaultOptions = (type) => (
    type === 'status'
      ? [
        { label: t('projectDetail.status_not_started'), color: 'grey' },
        { label: t('projectDetail.status_in_progress'), color: 'blue' },
        { label: t('projectDetail.status_done'), color: 'green', done: true },
      ]
      : [{ label: '', color: 'grey' }]
  );

  const typeChoices = isNew ? CREATABLE_TYPES : (CONVERTIBLE_TYPES.includes(column.type) ? CONVERTIBLE_TYPES : [column.type]);
  const typeChanged = !isNew && form.type !== column.type;
  const typeLocked = !isNew && !CONVERTIBLE_TYPES.includes(column.type);
  const showOptions = OPTION_TYPES.includes(form.type) && (isNew || !typeChanged);
  // "Modifica colonna" serve se c'è un tipo da cambiare o delle opzioni da gestire.
  const canEditProperty = !isNew && column.type !== 'title' && (!typeLocked || OPTION_TYPES.includes(column.type));

  const changeType = (type) => {
    setForm((prev) => ({ ...prev, type, options: isNew && OPTION_TYPES.includes(type) ? defaultOptions(type) : prev.options }));
  };

  const submit = (e) => {
    e.preventDefault();
    const name = form.name.trim() || (isNew ? t(`projectDetail.type_${form.type}`) : '');
    if (!name) return;
    const payload = { name };
    if (isNew || typeChanged) payload.type = form.type;
    // Con un cambio di tipo le opzioni nascono dai valori esistenti: non si inviano.
    if (showOptions) payload.options = form.options.filter((o) => o.label.trim());
    onSubmit(payload);
  };

  // Scelta del tipo e opzioni: uguali nella creazione e in "Modifica colonna".
  const typeAndOptions = (
    <>
      {column?.type !== 'title' && (
        <>
          <label className="pd-popover-label">{t('projectDetail.columnType')}</label>
          <div className="cm-types" role="radiogroup" aria-label={t('projectDetail.columnType')}>
            {TYPE_GROUPS.map(([group, types]) => {
              const visible = types.filter((type) => typeChoices.includes(type));
              if (visible.length === 0) return null;
              return (
                <div key={group} className="cm-type-group">
                  {isNew && <div className="cm-type-heading">{t(`projectDetail.typeGroup_${group}`)}</div>}
                  <div className="cm-type-grid">
                    {visible.map((type) => (
                      <button
                        key={type}
                        type="button"
                        role="radio"
                        aria-checked={form.type === type}
                        disabled={typeLocked}
                        className={'cm-type' + (form.type === type ? ' cm-type-active' : '')}
                        onClick={() => changeType(type)}
                      >
                        <NtIcon name={TYPE_ICONS[type]} size={16} />
                        <span>{t(`projectDetail.type_${type}`)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {typeChanged && <p className="nt-muted pd-popover-hint">{t('projectDetail.typeChangeHint')}</p>}
        </>
      )}

      {showOptions && (
        <>
          <label className="pd-popover-label">{t('projectDetail.columnOptions')}</label>
          <ColumnOptionsEditor options={form.options} withDone={form.type === 'status'} onChange={(options) => setForm({ ...form, options })} />
        </>
      )}

      <div className="pd-popover-actions">
        <button type="submit" className="btn" disabled={(!isNew && !form.name.trim()) || (showOptions && !form.options.some((o) => o.label.trim()))}>{isNew ? t('projectDetail.addColumn') : t('common.save')}</button>
      </div>
    </>
  );

  const popoverStyle = place ? { position: 'fixed', top: place.top, left: place.left, right: 'auto', maxHeight: place.maxHeight } : { visibility: 'hidden' };

  // --- Colonna nuova: nome, tipo, opzioni ---
  if (isNew) {
    return (
      <>
        <div className="pd-overlay" onClick={onClose} />
        <form className={'pd-popover cm-popover' + (showOptions ? ' pd-popover-wide' : '')} onSubmit={submit} ref={formRef} style={popoverStyle}>
          <div className="cm-title">{t('projectDetail.newColumn')}</div>
          <label className="pd-popover-label">{t('projectDetail.columnName')}</label>
          <input
            autoFocus
            value={form.name}
            placeholder={t(`projectDetail.type_${form.type}`)}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={100}
          />
          {typeAndOptions}
        </form>
      </>
    );
  }

  // --- Colonna esistente: menu in stile Notion ---
  const byKey = Object.fromEntries((actions || []).map((a) => [a.key, a]));
  const nameChanged = form.name.trim() !== '' && form.name.trim() !== column.name;
  // Rinominare: Invio, oppure chiudendo il menu con il nome già modificato.
  const commitName = () => onSubmit({ name: form.name.trim() });
  const close = () => (nameChanged ? commitName() : onClose());

  const item = (key, { chevron = false, danger = false, onClick } = {}) => {
    const action = byKey[key];
    if (!action && !onClick) return null;
    return (
      <button
        key={key}
        type="button"
        className={'cmn-item' + (danger ? ' cmn-item-danger' : '')}
        onClick={onClick || action.onClick}
      >
        <NtIcon name={action.icon} size={16} />
        <span className="cmn-item-label">{action.label}</span>
        {chevron && <NtIcon name="chevronRight" size={14} />}
      </button>
    );
  };

  const subHead = (title) => (
    <div className="cmn-subhead">
      <button type="button" className="cmn-back" onClick={() => setView('main')} aria-label={t('projectView.menuBack')} title={t('projectView.menuBack')}>
        <NtIcon name="back" size={16} />
      </button>
      <span>{title}</span>
    </div>
  );

  let body;
  if (view === 'edit') {
    body = (
      <>
        {subHead(t('projectView.menuEditColumn'))}
        {typeAndOptions}
      </>
    );
  } else if (view === 'sort') {
    body = (
      <>
        {subHead(t('projectView.menuSort'))}
        <div className="cmn-list">
          {item('asc')}
          {item('desc')}
        </div>
      </>
    );
  } else {
    const sortItem = byKey.asc && byKey.desc ? (
      <button type="button" className="cmn-item" onClick={() => setView('sort')}>
        <NtIcon name="sort" size={16} />
        <span className="cmn-item-label">{t('projectView.menuSort')}</span>
        <NtIcon name="chevronRight" size={14} />
      </button>
    ) : null;
    body = (
      <>
        <div className="cmn-head">
          <span className="cmn-type" title={t(`projectDetail.type_${column.type}`)}>
            <NtIcon name={TYPE_ICONS[column.type]} size={16} />
          </span>
          <input
            autoFocus
            className="cmn-name"
            value={form.name}
            maxLength={100}
            aria-label={t('projectDetail.columnName')}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (nameChanged) commitName();
                else onClose();
              }
            }}
          />
        </div>
        <div className="cmn-list">
          {canEditProperty && (
            <button type="button" className="cmn-item" onClick={() => setView('edit')}>
              <NtIcon name="sliders" size={16} />
              <span className="cmn-item-label">{t('projectView.menuEditColumn')}</span>
              <NtIcon name="chevronRight" size={14} />
            </button>
          )}
          <div className="cmn-sep" />
          {item('filter')}
          {sortItem}
          {item('group')}
          {item('hide')}
          <div className="cmn-sep" />
          {item('insertLeft')}
          {item('insertRight')}
          {item('dup')}
          {item('del', { danger: true })}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pd-overlay" onClick={close} />
      <form
        className={'pd-popover cmn' + (view === 'edit' ? ' cm-popover' + (showOptions ? ' pd-popover-wide' : '') : '')}
        onSubmit={submit}
        ref={formRef}
        style={popoverStyle}
      >
        {body}
      </form>
    </>
  );
}

export default ColumnMenu;
